import {
  UNIT_GALLERY_LOOKUP_KEYS,
  type PropertyImage,
} from '@/core/services/contentfulClient';

/** Listing field that will match a Contentful `unitModel` key. Absent until the API sends it. */
export function readListingUnitModel(
  unit: object | null | undefined,
): string | undefined {
  if (!unit || !('unit_model' in unit)) {
    return undefined;
  }
  const value = (unit as { unit_model?: unknown }).unit_model;
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed || undefined;
}

const galleryUriCache = new WeakMap<object, { uri: string }[]>();

function toGalleryUris(
  pool: readonly (
    | PropertyImage
    | { readonly url?: string; readonly uri?: string }
  )[],
): { uri: string }[] {
  const cached = galleryUriCache.get(pool);
  if (cached) {
    return cached;
  }
  const uris: { uri: string }[] = [];
  for (const img of pool) {
    let uri = '';
    if ('uri' in img && typeof img.uri === 'string') {
      uri = img.uri.trim();
    } else if ('url' in img && typeof img.url === 'string') {
      uri = img.url.trim();
    }
    if (uri) {
      uris.push({ uri });
    }
  }
  galleryUriCache.set(pool, uris);
  return uris;
}

function selectUnitGalleryPool(
  lookup: Readonly<Record<string, readonly PropertyImage[]>> | null | undefined,
  unitModel: string | undefined,
):
  | { readonly pool: readonly (PropertyImage | { readonly url?: string; readonly uri?: string })[]; readonly kind: 'model' | 'unit' | 'property' }
  | undefined {
  const modelKey = unitModel?.trim().toLowerCase();
  const isReservedKey =
    modelKey === UNIT_GALLERY_LOOKUP_KEYS.unitImages.toLowerCase() ||
    modelKey === UNIT_GALLERY_LOOKUP_KEYS.propertyImages.toLowerCase();
  const modelImages =
    modelKey && !isReservedKey ? lookup?.[modelKey] : undefined;
  if (modelImages && modelImages.length > 0) {
    return { pool: modelImages, kind: 'model' };
  }

  const unitImages = lookup?.[UNIT_GALLERY_LOOKUP_KEYS.unitImages] ?? [];
  if (unitImages.length > 0) {
    return { pool: unitImages, kind: 'unit' };
  }

  const propertyImages = lookup?.[UNIT_GALLERY_LOOKUP_KEYS.propertyImages] ?? [];
  if (propertyImages.length > 0) {
    return { pool: propertyImages, kind: 'property' };
  }

  return undefined;
}

/**
 * Unit gallery from the Contentful lookup:
 * matching `unit_model` (both sides lowercased) → all `unitImages` in a per-unit order → `propertyImages` → empty
 * (callers keep the existing placeholder when this returns []).
 * Detail screens use this. Listing cards must use {@link resolveListingCardImages}.
 */
export function resolveUnitListingImages(
  lookup: Readonly<Record<string, readonly PropertyImage[]>> | null | undefined,
  unitModel: string | undefined,
  unitId: string,
): { uri: string }[] {
  const selected = selectUnitGalleryPool(lookup, unitModel);
  if (!selected) {
    return [];
  }
  if (selected.kind === 'unit') {
    return pickMixedUnitImages(selected.pool, unitId, selected.pool.length);
  }
  return toGalleryUris(selected.pool);
}

/**
 * Listing-card gallery. Same Contentful priority as {@link resolveUnitListingImages},
 * but only the stable 3-image slice. Passing the whole `unitImages` pool into every
 * card makes `ImageCarousel` mount that many slides per unit and freezes the page.
 */
export function resolveListingCardImages(
  lookup: Readonly<Record<string, readonly PropertyImage[]>> | null | undefined,
  unitModel: string | undefined,
  unitId: string,
): { uri: string }[] {
  const selected = selectUnitGalleryPool(lookup, unitModel);
  if (!selected) {
    return [];
  }
  return pickMixedUnitImages(selected.pool, unitId);
}

/**
 * Stable pseudo-random mix: same `unitId` always gets the same slice from the
 * project's Contentful `propertyImages.fields.unitImages` pool.
 */
export function pickMixedUnitImages(
  pool: readonly (
    | PropertyImage
    | { readonly url?: string; readonly uri?: string }
  )[],
  unitId: string,
  count = 3,
): { uri: string }[] {
  const uris = toGalleryUris(pool).map((img) => img.uri);

  if (uris.length === 0 || !unitId.trim()) {
    return [];
  }

  let seed = 2166136261;
  for (let i = 0; i < unitId.length; i += 1) {
    seed ^= unitId.codePointAt(i) ?? 0;
    seed = Math.imul(seed, 16777619);
  }

  const indices = uris.map((_, index) => index);
  for (let i = indices.length - 1; i > 0; i -= 1) {
    seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
    const j = seed % (i + 1);
    const current = indices[i]!;
    indices[i] = indices[j]!;
    indices[j] = current;
  }

  const take = Math.min(Math.max(count, 1), uris.length);
  const picked: { uri: string }[] = [];
  for (let i = 0; i < take; i += 1) {
    const uri = uris[indices[i]!]!;
    picked.push({ uri });
  }
  return picked;
}
